/**
 * AnnouncementModal Component
 * 
 * Displays announcements in a centered overlay with CareVault design system styling.
 * Supports rich content: images, videos, buttons, and polls.
 * 
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5, 4.6, 4.7
 */

import { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ThemeBlob, BLOB_PATHS } from '@/components/ui/theme-blob';
import type { Announcement, PollResults, PollOption } from '@/lib/announcement-types';

export interface AnnouncementModalProps {
  announcement: Announcement;
  onClose: () => void;
  onDismiss: () => void;
  onButtonClick: (buttonId: string, url: string) => void;
  onPollSubmit: (optionId: string) => Promise<PollResults | null>;
  pollResults?: PollResults | null;
  hasSubmittedPoll?: boolean;
}

export function AnnouncementModal({
  announcement,
  onClose,
  onDismiss,
  onButtonClick,
  onPollSubmit,
  pollResults: initialPollResults = null,
  hasSubmittedPoll: initialHasSubmitted = false,
}: AnnouncementModalProps) {
  const [selectedPollOption, setSelectedPollOption] = useState<string | null>(null);
  const [isSubmittingPoll, setIsSubmittingPoll] = useState(false);
  const [pollResults, setPollResults] = useState<PollResults | null>(initialPollResults);
  const [hasSubmittedPoll, setHasSubmittedPoll] = useState(initialHasSubmitted);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Update poll state when props change
  useEffect(() => {
    setPollResults(initialPollResults);
    setHasSubmittedPoll(initialHasSubmitted);
  }, [initialPollResults, initialHasSubmitted]);

  // Handle Escape key to close modal (Requirement 4.7)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onDismiss();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onDismiss]);

  // Handle backdrop click (Requirement 4.7)
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onDismiss();
    }
  }, [onDismiss]);

  // Handle poll submission (Requirement 4.6)
  const handlePollSubmit = async () => {
    if (!selectedPollOption || isSubmittingPoll) return;

    setIsSubmittingPoll(true);
    try {
      const results = await onPollSubmit(selectedPollOption);
      if (results) {
        setPollResults(results);
        setHasSubmittedPoll(true);
      }
    } catch (error) {
      console.error('Failed to submit poll:', error);
    } finally {
      setIsSubmittingPoll(false);
    }
  };

  // Get YouTube embed URL
  const getYouTubeEmbedUrl = (url: string): string => {
    const videoId = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&\s]+)/)?.[1];
    return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
  };

  // Get Vimeo embed URL
  const getVimeoEmbedUrl = (url: string): string => {
    const videoId = url.match(/vimeo\.com\/(?:video\/)?(\d+)/)?.[1];
    return videoId ? `https://player.vimeo.com/video/${videoId}` : url;
  };

  // Render video based on type (Requirement 4.4)
  const renderVideo = () => {
    if (!announcement.video_url) return null;

    let embedUrl = announcement.video_url;
    
    if (announcement.video_type === 'youtube') {
      embedUrl = getYouTubeEmbedUrl(announcement.video_url);
    } else if (announcement.video_type === 'vimeo') {
      embedUrl = getVimeoEmbedUrl(announcement.video_url);
    }

    if (announcement.video_type === 'direct') {
      return (
        <video
          src={announcement.video_url}
          controls
          className="w-full rounded-lg max-h-64 object-contain bg-black"
          preload="metadata"
        >
          Your browser does not support the video tag.
        </video>
      );
    }

    return (
      <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-black">
        <iframe
          src={embedUrl}
          title={announcement.title}
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  };

  // Render poll component (Requirement 4.6)
  const renderPoll = () => {
    if (!announcement.poll) return null;

    const { question, options } = announcement.poll;

    return (
      <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
        <p className="font-medium text-gray-900 dark:text-gray-100 mb-3">{question}</p>
        
        <div className="space-y-2">
          {options.map((option: PollOption) => {
            const isSelected = selectedPollOption === option.id;
            const percentage = pollResults?.percentages?.[option.id] || 0;
            const count = pollResults?.options?.[option.id] || 0;

            if (hasSubmittedPoll && pollResults) {
              // Show results view
              return (
                <div
                  key={option.id}
                  className="relative overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700"
                >
                  <div
                    className="absolute inset-0 bg-primary/20 transition-all duration-500"
                    style={{ width: `${percentage}%` }}
                  />
                  <div className="relative px-4 py-3 flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {option.text}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {percentage}% ({count})
                    </span>
                  </div>
                </div>
              );
            }

            // Show selection view
            return (
              <button
                key={option.id}
                onClick={() => setSelectedPollOption(option.id)}
                className={cn(
                  "w-full px-4 py-3 rounded-lg border-2 text-left transition-all",
                  "hover:border-primary/50 hover:bg-primary/5",
                  isSelected
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                )}
              >
                <span className="text-sm font-medium">{option.text}</span>
              </button>
            );
          })}
        </div>

        {!hasSubmittedPoll && (
          <button
            onClick={handlePollSubmit}
            disabled={!selectedPollOption || isSubmittingPoll}
            className={cn(
              "mt-4 w-full py-2.5 rounded-full font-medium text-sm transition-all",
              "bg-primary text-white hover:bg-primary/90",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
          >
            {isSubmittingPoll ? 'Submitting...' : 'Submit Vote'}
          </button>
        )}

        {hasSubmittedPoll && pollResults && (
          <p className="mt-3 text-xs text-center text-gray-500 dark:text-gray-400">
            Total responses: {pollResults.total_responses}
          </p>
        )}
      </div>
    );
  };

  // Render action buttons (Requirement 4.5)
  const renderButtons = () => {
    if (!announcement.buttons || announcement.buttons.length === 0) return null;

    return (
      <div className="mt-4 flex flex-wrap gap-3">
        {announcement.buttons.map((button) => (
          <button
            key={button.id}
            onClick={() => onButtonClick(button.id, button.url)}
            className={cn(
              "px-6 py-2.5 rounded-full font-medium text-sm transition-all",
              button.style === 'primary'
                ? "bg-primary text-white hover:bg-primary/90"
                : "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-700"
            )}
          >
            {button.text}
          </button>
        ))}
      </div>
    );
  };

  return (
    // Backdrop with semi-transparent overlay (Requirement 4.1)
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="announcement-title"
    >
      {/* Modal content with CareVault design system (Requirement 4.2) */}
      <div
        className={cn(
          "relative w-full max-w-lg max-h-[90vh] overflow-hidden",
          "bg-white dark:bg-gray-900 rounded-3xl shadow-2xl",
          "animate-in zoom-in-95 slide-in-from-bottom-4 duration-300"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Organic blob decorations (Requirement 4.2) */}
        <div className="absolute -top-16 -left-16 w-48 h-48 opacity-30 pointer-events-none">
          <ThemeBlob
            id="announcement-blob-1"
            path={BLOB_PATHS.blob1}
            colorVariant="primary"
            opacity={0.4}
          />
        </div>
        <div className="absolute -bottom-12 -right-12 w-40 h-40 opacity-20 pointer-events-none">
          <ThemeBlob
            id="announcement-blob-2"
            path={BLOB_PATHS.blob3}
            colorVariant="secondary"
            opacity={0.3}
          />
        </div>

        {/* Close button (Requirement 4.7) */}
        <button
          onClick={onClose}
          className={cn(
            "absolute top-4 right-4 z-10 p-2 rounded-full",
            "bg-gray-100/80 dark:bg-gray-800/80 backdrop-blur-sm",
            "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100",
            "transition-colors"
          )}
          aria-label="Close announcement"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Scrollable content area */}
        <div className="relative z-10 max-h-[90vh] overflow-y-auto p-6">
          {/* Image with lazy loading (Requirement 4.3) */}
          {announcement.image_url && (
            <div className="mb-4 -mx-6 -mt-6">
              <div className={cn(
                "relative w-full aspect-video bg-gray-100 dark:bg-gray-800",
                !imageLoaded && "animate-pulse"
              )}>
                <img
                  src={announcement.image_url}
                  alt={announcement.title}
                  loading="lazy"
                  onLoad={() => setImageLoaded(true)}
                  className={cn(
                    "w-full h-full object-cover transition-opacity duration-300",
                    imageLoaded ? "opacity-100" : "opacity-0"
                  )}
                />
              </div>
            </div>
          )}

          {/* Video embed (Requirement 4.4) */}
          {announcement.video_url && (
            <div className="mb-4">
              {renderVideo()}
            </div>
          )}

          {/* Title */}
          <h2
            id="announcement-title"
            className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2"
          >
            {announcement.title}
          </h2>

          {/* Body content */}
          <div className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed whitespace-pre-wrap">
            {announcement.body}
          </div>

          {/* Poll component (Requirement 4.6) */}
          {renderPoll()}

          {/* Action buttons (Requirement 4.5) */}
          {renderButtons()}

          {/* Dismiss button */}
          <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-800">
            <button
              onClick={onDismiss}
              className="w-full py-2.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AnnouncementModal;
